import { BigNumber } from '@ethersproject/bignumber'
import { L2_DEADLINE_FROM_NOW } from 'constants/misc'
import { useAccount } from 'hooks/useAccount'
import { useInterfaceMulticall } from 'hooks/useContract'
import useCurrentBlockTimestamp from 'hooks/useCurrentBlockTimestamp'
import { useCallback, useMemo } from 'react'
import { useAppSelector } from 'state/hooks'
import { isL2ChainId } from 'uniswap/src/features/chains/utils'

export default function useTransactionDeadline(): BigNumber | undefined {
  const { chainId } = useAccount()
  const ttl = useAppSelector((state) => state.user.userDeadline)
  const blockTimestamp = useCurrentBlockTimestamp()
  return useMemo(() => timestampToDeadline(chainId, blockTimestamp, ttl), [blockTimestamp, chainId, ttl])
}

/**
 * Returns an asynchronous function which will get the block timestamp and combine it with user settings for a deadline.
 * Should be used for any submitted transactions, as it uses an on-chain timestamp instead of a client timestamp.
 */
export function useGetTransactionDeadline(): () => Promise<BigNumber | undefined> {
  const { chainId } = useAccount()
  const ttl = useAppSelector((state) => state.user.userDeadline)
  const multicall = useInterfaceMulticall()
  return useCallback(async () => {
    // Prefer the on-chain block timestamp (via Uniswap's UniswapInterfaceMulticall lens), but that
    // lens contract isn't deployed on every chain — `useInterfaceMulticall()` returns null when the
    // chain is absent from @uniswap/sdk-core's MULTICALL_ADDRESSES (e.g. Robinhood Chain 4663).
    // Falling back to the client wall-clock keeps deadlines working there; a deadline is a coarse
    // upper bound (minutes), so sub-second block/clock skew is immaterial. Without this fallback,
    // `null.getCurrentBlockTimestamp()` throws and every swap on such a chain fails before submit.
    const blockTimestamp = multicall
      ? await multicall.getCurrentBlockTimestamp()
      : BigNumber.from(Math.floor(Date.now() / 1000))
    return timestampToDeadline(chainId, blockTimestamp, ttl)
  }, [chainId, multicall, ttl])
}

function timestampToDeadline(chainId?: number, blockTimestamp?: BigNumber, ttl?: number) {
  if (blockTimestamp && isL2ChainId(chainId)) {
    return blockTimestamp.add(L2_DEADLINE_FROM_NOW)
  }
  if (blockTimestamp && ttl) {
    return blockTimestamp.add(ttl)
  }
  return undefined
}
